<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item :to="{ name: 'DashboardHome' }">
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item :to="{ name: 'PartnerMarketingEventList' }">點數/商品兌換活動</b-breadcrumb-item>
            <b-breadcrumb-item active>{{ isEdit ? '編輯' : '新增' }}點數/商品兌換活動</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-6 mb-2 mb-xl-0 font-weight-bold">
          {{ isEdit ? '編輯' : '新增' }}點數/商品兌換活動
        </h4>
      </div>
      <b-overlay :show="isLoading">
        <b-form @submit.prevent="onSubmit" ref="formRef">
          <b-form-group label-cols="12" label-cols-lg="2" label-size="sm" label="活動名稱" label-for="name">
            <b-form-input id="name" v-model="form.name" :state="errors.name ? false : null" :disabled="isView || isLoading" />
            <b-form-invalid-feedback v-if="errors.name">{{ errors.name }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-lg="2" label-size="sm" label="圖片" label-for="image_url">
            <div class="image-selector">
              <div class="image" v-if="form.image_url">
                <img class="img-fluid" :src="form.image_url" style="max-width: 140px; max-height: 80px;" />
                <button v-if="!isView && !isLoading" class="btn btn-danger" type="button" @click="form.image_url = null">
                  <i class="fa fa-times m-0"></i>
                </button>
              </div>
              <div class="selector" v-else>
                <ImageSelector v-if="!isView && !isLoading" @input="uploadImage">
                  <template #trigger="{ openFileSelector }">
                    <button type="button" class="btn s-trigger-btn" @click="openFileSelector">
                      <i class="fa fa-plus"></i> 圖片
                    </button>
                  </template>
                </ImageSelector>
              </div>
            </div>
            <small style="color: #7f8182;">建議尺寸：300 x 300px，格式：jpg</small>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-lg="2" label-size="sm" label="狀態" label-for="status">
            <b-form-select v-model="form.status" :options="statusOptions" :state="errors.status ? false : null" :disabled="isView || isLoading" />
            <b-form-invalid-feedback v-if="errors.status">{{ errors.status }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-lg="2" label-size="sm" label="類型" label-for="type" :state="errors.type ? false : null">
            <b-form-radio-group id="type" v-model="form.type" :options="typeOptions" :disabled="isView || isLoading" />
            <b-form-invalid-feedback v-if="errors.type">{{ errors.type }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-lg="2" label-size="sm" label="開始時間" label-for="start_at">
            <div class="d-flex flex-column flex-xl-row align-items-start align-items-xl-center">
              <datepicker
                placeholder="選擇日期"
                v-model="start_at"
                bootstrap-styling
                format="yyyy-MM-dd"
                :language="zh"
                :disabled="isView || isLoading"
                clear-button
                :disabledDates="startAtDisabledDates"
              />
            </div>
            <b-form-invalid-feedback v-if="errors.start_at">{{ errors.start_at }}</b-form-invalid-feedback>
            <b-form-invalid-feedback :state="startAtValidationState">{{ startAtValidationMessage }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-lg="2" label-size="sm" label="結束時間" label-for="end_at">
            <div class="d-flex flex-column flex-xl-row align-items-start align-items-xl-center">
              <datepicker
                placeholder="選擇日期"
                v-model="end_at"
                bootstrap-styling
                format="yyyy-MM-dd"
                :language="zh"
                :disabled="isView || isLoading"
                clear-button
                :disabledDates="endAtDisabledDates"
              />
            </div>
            <b-form-invalid-feedback v-if="errors.end_at">{{ errors.end_at }}</b-form-invalid-feedback>
            <b-form-invalid-feedback :state="endAtValidationState">{{ endAtValidationMessage }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-lg="2" label-size="sm" label="兌換注意事項" label-for="notes">
            <classic-editor
              :extra-plugins="[MyCustomUploadAdapterPlugin]"
              v-model="form.notes"
              :disabled="isView || isLoading"
            ></classic-editor>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-lg="2" label-size="sm" label="排序" label-for="sort">
            <b-form-input id="sort" v-model="form.sort" type="number" placeholder="排序為必填" :state="errors.sort ? false : null" :disabled="isView || isLoading" />
            <b-form-invalid-feedback v-if="errors.sort">{{ errors.sort }}</b-form-invalid-feedback>
          </b-form-group>
          <div class="d-flex justify-content-center">
            <b-button class="mr-3" @click="goBackOrList" variant="outline-danger">返回</b-button>
            <b-button v-if="!isView" type="submit" variant="success">儲存</b-button>
          </div>
        </b-form>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import partnerMarketingEvent from '@/apis/partnerMarketingEvent'
import ImageSelector from '@/components/ImageSelector'
import Datepicker from "vuejs-datepicker"
import { zh } from "vuejs-datepicker/dist/locale"
import { format, set, isBefore, startOfDay } from "date-fns"
import { BButton, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormRadioGroup, BFormInvalidFeedback, BOverlay, BBreadcrumb, BBreadcrumbItem } from 'bootstrap-vue'
import ClassicEditor from "@/components/ClassicEditor.vue"
import imageApi from "@/apis/image"
export default {
  components: { ClassicEditor, ImageSelector, Datepicker, BButton, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormRadioGroup, BFormInvalidFeedback, BOverlay, BBreadcrumb, BBreadcrumbItem },
  data() {
    return {
      zh,
      start_at: new Date(),
      end_at: new Date(),
      form: {
        name: '',
        type: 'serial',
        image_url: '',
        status: 'public',
        start_at: '',
        end_at: '',
        notes: '',
        sort: null
      },
      typeOptions: [
        { text: '序號', value: 'serial' },
        { text: '商品', value: 'product' }
      ],
      statusOptions: [
        { text: '公開', value: 'public' },
        { text: '草稿', value: 'draft' },
        { text: '關閉', value: 'closed' }
      ],
      isEdit: false,
      isView: false,
      isLoading: false,
      errors: {},
    }
  },
  computed: {
    // 開始時間驗證狀態
    startAtValidationState() {
      if (!this.start_at) return null

      // 只在新增模式檢查是否早於目前時間
      if (!this.isEdit) {
        const today = startOfDay(new Date())
        const selectedDate = startOfDay(new Date(this.start_at))
        if (isBefore(selectedDate, today)) {
          return false
        }
      }

      return null
    },

    // 開始時間驗證訊息
    startAtValidationMessage() {
      if (!this.start_at) return ''

      if (!this.isEdit) {
        const today = startOfDay(new Date())
        const selectedDate = startOfDay(new Date(this.start_at))
        if (isBefore(selectedDate, today)) {
          return '開始時間 不得早於 目前時間'
        }
      }

      return ''
    },

    // 結束時間驗證狀態
    endAtValidationState() {
      if (!this.end_at) return null

      // 檢查是否早於目前時間
      const today = startOfDay(new Date())
      const selectedEndDate = startOfDay(new Date(this.end_at))
      if (isBefore(selectedEndDate, today)) {
        return false
      }

      // 檢查是否早於開始時間
      if (this.start_at && this.end_at) {
        const startDate = startOfDay(new Date(this.start_at))
        const endDate = startOfDay(new Date(this.end_at))
        if (isBefore(endDate, startDate)) {
          return false
        }
      }

      return null
    },

    // 結束時間驗證訊息
    endAtValidationMessage() {
      if (!this.end_at) return ''

      // 檢查是否早於目前時間
      const today = startOfDay(new Date())
      const selectedEndDate = startOfDay(new Date(this.end_at))
      if (isBefore(selectedEndDate, today)) {
        return '結束時間 不得早於 目前時間'
      }

      // 檢查是否早於開始時間
      if (this.start_at && this.end_at) {
        const startDate = startOfDay(new Date(this.start_at))
        const endDate = startOfDay(new Date(this.end_at))
        if (isBefore(endDate, startDate)) {
          return '結束時間 不得早於 開始時間'
        }
      }

      return ''
    },

    // 開始時間禁用日期
    startAtDisabledDates() {
      // 只在新增模式禁用過去日期
      if (!this.isEdit) {
        return {
          to: new Date(new Date().setHours(0, 0, 0, 0) - 1)
        }
      }
      return {}
    },

    // 結束時間禁用日期
    endAtDisabledDates() {
      return {
        to: new Date(new Date().setHours(0, 0, 0, 0) - 1)
      }
    }
  },
  created() {
    this.isEdit = !!this.$route.params.partnerMarketingEventId && this.$route.name !== 'PartnerMarketingEventCreate'
    this.isView = this.$route.name === 'PartnerMarketingEventView'
    if (this.isEdit) {
      this.isLoading = true
      partnerMarketingEvent.getPartnerMarketingEvent(this.$route.params.partnerMarketingEventId).then(res => {
        const data = res.data.data
        Object.assign(this.form, data)
        this.start_at = data.start_at && new Date(data.start_at)
        this.end_at = data.end_at && new Date(data.end_at)
      }).finally(() => {
        this.isLoading = false
      })
    } else {
      // 新增模式：取得目前最大 sort 值並設定預設值
      this.isLoading = true
      partnerMarketingEvent.getMaxSort().then(res => {
        const maxSort = res.data.data.max_sort || 0
        this.form.sort = maxSort + 1
      }).catch(() => {
        // 如果取得失敗，預設值為 1
        this.form.sort = 1
      }).finally(() => {
        this.isLoading = false
      })
    }
  },
  methods: {
    goBackOrList() {
      this.$router.back()
    },
    handleFormatDates() {
      this.form.start_at = this.doHandleFormatDates(this.start_at, 0, 0, 0)
      this.form.end_at = this.doHandleFormatDates(this.end_at, 23, 59, 59)
    },
    doHandleFormatDates(datetime, hours, minutes, seconds) {
      if (!datetime) return null

      return format(
        set(new Date(datetime), {
          hours,
          minutes,
          seconds,
        }),
        "yyyy-MM-dd HH:mm:ss"
      )
    },
    uploadImage(imageBase64) {
      this.isLoading = true
      partnerMarketingEvent.uploadImageByBase64({ image: imageBase64 }).then(res => {
        this.form.image_url = res.data.data.url
      }).finally(() => {
        this.isLoading = false
      })
    },
    onSubmit() {
      this.errors = {}
      if (!this.form.name) this.errors.name = '請輸入名稱'
      if (!this.form.type) this.errors.type = '請選擇類型'
      if (!this.form.status) this.errors.status = '請選擇狀態'
      if (!this.start_at) this.errors.start_at = '請選擇開始時間'
      if (!this.end_at) this.errors.end_at = '請選擇結束時間'
      if (!this.form.sort) this.errors.sort = '此欄位為必填'

      // 檢查時間驗證
      if (this.startAtValidationState === false) {
        this.errors.start_at = this.startAtValidationMessage
      }
      if (this.endAtValidationState === false) {
        this.errors.end_at = this.endAtValidationMessage
      }

      if (Object.keys(this.errors).length > 0) return
      this.isLoading = true

      this.handleFormatDates()

      const action = this.isEdit
        ? partnerMarketingEvent.updatePartnerMarketingEvent(this.$route.params.partnerMarketingEventId, this.form)
        : partnerMarketingEvent.createPartnerMarketingEvent(this.form)
      action.then(() => {
        this.$bvToast.toast('儲存成功', { variant: 'success', solid: true })
        this.goBackOrList()
      }).finally(() => {
        this.isLoading = false
      })
    },
    async MyCustomUploadAdapterPlugin(editor) {
      editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
        return {
          upload: () => {
            return new Promise((resolve, reject) => {
              loader.file.then((file) => {
                imageApi.upload(file)
                  .then((response) => {
                    const imageUrl = response.data.data.image.url
                    resolve({ default: imageUrl })
                  })
                  .catch((error) => {
                    console.error('圖片上傳失敗', error)
                    reject(error)
                  })
              })
            })
          },
        }
      }
    }
  }
}
</script>

<style scoped>
.notes-textarea {
  line-height: 1.5;
}
</style>
